"use client";
import React, { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

interface ShrinkOnScrollProps {
  children: React.ReactNode;
  /** Minimum scale when fully scrolled (default: 0.9) */
  minScale?: number;
  /** Final rounded bottom value in px (default: 90px) */
  maxRounded?: number;
  /** Percentage of scroll (0–1) before shrinking starts (default: 0.1 = 10%) */
  triggerOffset?: number;
  /** Tailwind classes for section styling */
  className?: string;
}

const ShrinkOnScroll: React.FC<ShrinkOnScrollProps> = ({
  children,
  minScale = 0.91,
  maxRounded = 90,
  triggerOffset = 0.1,
  className = "",
}) => {
  const sectionRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!sectionRef.current || !contentRef.current) return;

    const ctx = gsap.context(() => {
      const section = sectionRef.current!;
      const content = contentRef.current!;

      gsap.fromTo(
        content,
        { scale: 1, borderBottomLeftRadius: 0, borderBottomRightRadius: 0 },
        {
          scale: minScale,
          borderBottomLeftRadius: maxRounded,
          borderBottomRightRadius: maxRounded,
          ease: "none",
          scrollTrigger: {
            trigger: section,
            start: `top+=${section.offsetHeight * triggerOffset} top`,
            end: "bottom top",
            scrub: true,
          },
        }
      );
    }, sectionRef);

    return () => ctx.revert();
  }, [minScale, maxRounded, triggerOffset]);

  return (
    <section ref={sectionRef} className={`h-screen w-screen ${className}`}>
      <div
        ref={contentRef}
        className="w-screen h-screen origin-center overflow-hidden"
      >
        {children}
      </div>
    </section>
  );
};

export default ShrinkOnScroll;
